import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import {
  Badge,
  Button,
  Form,
  Modal,
  Spinner,
} from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import useAdminGuard from '../../hooks/useAdminGuard';
import { hasPermission, PERMISSION_IDS } from '../../utils/permissions';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import '../../styles/admin.css';
import './AdminEventosPage.css';
import { logError } from '../../utils/logger';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
  return `${base}/uploads/${sanitized}`;
};

const EventSkeleton = () => (
  <div className="AEP-skeleton">
    <div className="AEP-skeleton-image" />
    <div className="AEP-skeleton-body">
      <div className="AEP-skeleton-line AEP-skeleton-line--short" />
      <div className="AEP-skeleton-line AEP-skeleton-line--medium" />
      <div className="AEP-skeleton-line AEP-skeleton-line--long" />
      <div className="AEP-skeleton-line AEP-skeleton-line--medium" />
    </div>
  </div>
);

const StatsCard = ({ icon, label, value, variant }) => (
  <div className="AEP-stat-card">
    <div className={classNames('AEP-stat-icon', `AEP-stat-icon--${variant}`)}>
      <i className={`bi ${icon}`} aria-hidden="true" />
    </div>
    <div className="AEP-stat-content">
      <div className="AEP-stat-value">{value}</div>
      <div className="AEP-stat-label">{label}</div>
    </div>
  </div>
);

const EmptyState = ({ onCreate, showArchived }) => (
  <div className="AEP-empty-state">
    <i className="bi-calendar-event AEP-empty-icon" />
    <h5 className="AEP-empty-title">
      {showArchived ? 'Aún no hay eventos archivados' : 'Aún no hay eventos'}
    </h5>
    <p className="AEP-empty-description">
      {showArchived
        ? 'No hay eventos archivados en este momento.'
        : 'Crea tu primer evento para comenzar a gestionar eventos en la plataforma.'}
    </p>
    {!showArchived && (
      <Button variant="primary" onClick={onCreate}>
        <i className="bi-plus-circle me-2" />
        Crear evento
      </Button>
    )}
  </div>
);

const AdminEventosPageInner = () => {
  const { token, user } = useAdminGuard();
  const { notify } = useAdminNotifications();
  const navigate = useNavigate();

  // Verificar permisos específicos de eventos
  const hasViewEvents = useMemo(() => hasPermission(user, PERMISSION_IDS.VER_EVENTOS), [user]);
  const hasViewArchived = useMemo(() => hasPermission(user, PERMISSION_IDS.VER_EVENTOS_ARCHIVADOS), [user]);
  const hasCreateEvents = useMemo(() => hasPermission(user, PERMISSION_IDS.CREAR_EVENTOS), [user]);
  const hasEditEvents = useMemo(() => hasPermission(user, PERMISSION_IDS.EDITAR_EVENTOS), [user]);
  const hasDeleteEvents = useMemo(() => hasPermission(user, PERMISSION_IDS.ELIMINAR_EVENTOS), [user]);
  const hasArchiveEvents = useMemo(() => hasPermission(user, PERMISSION_IDS.ARCHIVAR_EVENTOS), [user]);
  const hasActivateEvents = useMemo(() => hasPermission(user, PERMISSION_IDS.ACTIVAR_EVENTOS), [user]);

  // Si no tiene permiso para ver eventos, redirigir
  useEffect(() => {
    if (user && !hasViewEvents) {
      notify({
        title: 'Acceso denegado',
        message: 'No tienes permisos para acceder a la gestión de eventos.',
        variant: 'error',
      });
      navigate('/403', { replace: true });
    }
  }, [user, hasViewEvents, navigate, notify]);

  const [stats, setStats] = useState({ activeEvents: 0, inactiveEvents: 0, archivedEvents: 0 });
  const [events, setEvents] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createEventTitle, setCreateEventTitle] = useState('');
  const [creatingEvent, setCreatingEvent] = useState(false);

  const fetchStats = async (isRefresh = false) => {
    if (!token) return;
    if (!isRefresh) setLoadingStats(true);
    try {
      const response = await fetch(buildApiUrl('/api/events/stats/overview'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 429) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const retryResponse = await fetch(buildApiUrl('/api/events/stats/overview'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!retryResponse.ok) {
          throw new Error(`Error al cargar estadísticas (${retryResponse.status})`);
        }
        const data = await retryResponse.json();
        setStats({
          activeEvents: data.activeEvents ?? data.activos ?? 0,
          inactiveEvents: data.inactiveEvents ?? 0,
          archivedEvents: data.archivedEvents ?? data.archivados ?? 0,
        });
        return;
      }

      if (!response.ok) {
        throw new Error(`Error al cargar estadísticas (${response.status})`);
      }
      const data = await response.json();
      setStats({
        activeEvents: data.activeEvents ?? data.activos ?? 0,
        inactiveEvents: data.inactiveEvents ?? 0,
        archivedEvents: data.archivedEvents ?? data.archivados ?? 0,
      });
    } catch (error) {
      logError('[AdminEventosPage] Error fetching stats:', error);
      if (error.message && !error.message.includes('429')) {
        notify({
          title: 'No pudimos obtener las estadísticas',
          message: error.message,
          variant: 'error',
        });
      }
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchEvents = async (isRefresh = false) => {
    if (!token) return;
    if (!isRefresh) setLoadingEvents(true);
    try {
      const url = showArchived
        ? buildApiUrl('/api/events/archived')
        : buildApiUrl('/api/events');
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 429) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const retryResponse = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!retryResponse.ok) {
          throw new Error(`Error al cargar eventos (${retryResponse.status})`);
        }
        const data = await retryResponse.json();
        setEvents(data.events || []);
        return;
      }

      if (!response.ok) {
        throw new Error(`Error al cargar eventos (${response.status})`);
      }
      const data = await response.json();
      setEvents(data.events || []);
    } catch (error) {
      logError('[AdminEventosPage] Error fetching events:', error);
      if (error.message && !error.message.includes('429')) {
        notify({
          title: 'No pudimos cargar los eventos',
          message: error.message,
          variant: 'error',
        });
      }
    } finally {
      setLoadingEvents(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await Promise.all([fetchStats(), fetchEvents()]);
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, showArchived]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([fetchStats(true), fetchEvents(true)]);
    setIsRefreshing(false);
    notify({
      title: 'Datos actualizados',
      message: 'La lista de eventos y estadísticas se sincronizaron correctamente.',
      variant: 'success',
    });
  };

  const filteredEvents = useMemo(() => {
    if (!search.trim()) return events;
    const term = search.trim().toLowerCase();
    return events.filter((event) => {
      const title = event.tituloevento || event.nombre || '';
      const description = event.descripcionbreve || event.descripcionBreve || '';
      return (
        title.toLowerCase().includes(term) ||
        description.toLowerCase().includes(term) ||
        String(event.id).includes(term)
      );
    });
  }, [events, search]);

  const handleCreateEvent = async () => {
    if (!createEventTitle.trim()) {
      notify({
        title: 'Error',
        message: 'El título del evento es requerido.',
        variant: 'error',
      });
      return;
    }

    setCreatingEvent(true);
    try {
      const response = await fetch(buildApiUrl('/api/events'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nombre: createEventTitle.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al crear evento');
      }

      const data = await response.json();
      notify({
        title: 'Evento creado',
        message: 'El evento se creó exitosamente. Redirigiendo al detalle...',
        variant: 'success',
      });

      setShowCreateModal(false);
      setCreateEventTitle('');
      await Promise.all([fetchStats(true), fetchEvents(true)]);
      
      if (data.event?.id || data.eventId) {
        setTimeout(() => {
          navigate(`/admin/events/${data.event?.id || data.eventId}`);
        }, 1000);
      }
    } catch (error) {
      logError('Error creando evento:', error);
      notify({
        title: 'Error',
        message: error.message || 'No se pudo crear el evento.',
        variant: 'error',
      });
    } finally {
      setCreatingEvent(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este evento?')) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl(`/api/events/${eventId}`), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Error al eliminar evento');
      }

      notify({
        title: 'Éxito',
        message: 'Evento eliminado exitosamente.',
        variant: 'success',
      });

      await Promise.all([fetchStats(true), fetchEvents(true)]);
    } catch (error) {
      logError('Error eliminando evento:', error);
      notify({
        title: 'Error',
        message: 'No se pudo eliminar el evento.',
        variant: 'error',
      });
    }
  };

  const handleToggleArchive = async (eventId, currentArchiveStatus) => {
    try {
      const response = await fetch(buildApiUrl(`/api/events/${eventId}/archive`), {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          archivo: !currentArchiveStatus,
        }),
      });

      if (!response.ok) {
        throw new Error('Error al cambiar estado de archivo');
      }

      notify({
        title: 'Éxito',
        message: `Evento ${!currentArchiveStatus ? 'archivado' : 'desarchivado'} exitosamente.`,
        variant: 'success',
      });

      await Promise.all([fetchStats(true), fetchEvents(true)]);
    } catch (error) {
      logError('Error cambiando estado de archivo:', error);
      notify({
        title: 'Error',
        message: 'No se pudo cambiar el estado del evento.',
        variant: 'error',
      });
    }
  };

  const formatEventDate = (diaseventos) => {
    if (!diaseventos || !Array.isArray(diaseventos) || diaseventos.length === 0) {
      return 'Sin fecha especificada';
    }
    const firstDate = new Date(diaseventos[0]);
    if (isNaN(firstDate.getTime())) {
      return 'Fecha inválida';
    }
    return firstDate.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (!user) return null;

  return (
    <div className="admin-dashboard">
      <div className="admin-layout">
        <AdminSidebar
          activeKey="events"
          onNavigate={(item) => {
            if (item.path) {
              navigate(item.path);
              return;
            }
            notify({
              title: 'Sección en desarrollo',
              message: 'Estamos migrando esta sección al nuevo panel.',
              variant: 'info',
            });
          }}
        />
        <main className="admin-main">
          <div className="AEP-container">
            {/* Header */}
            <div className="AEP-header">
              <div className="AEP-header-content">
                <h1 className="AEP-title">
                  <i className="bi-calendar-event text-primary" />
                  Gestión de Eventos
                </h1>
                <p className="AEP-subtitle">
                  Administra y gestiona todos los eventos de la plataforma
                </p>
              </div>
              <div className="AEP-actions">
                {hasViewArchived && (
                  <Button
                    variant={showArchived ? 'secondary' : 'outline-secondary'}
                    onClick={() => setShowArchived(!showArchived)}
                  >
                    <i className={showArchived ? 'bi-list-ul me-2' : 'bi-archive me-2'} />
                    {showArchived ? 'Ver activos' : 'Ver archivados'}
                  </Button>
                )}
                {hasCreateEvents && (
                  <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                    <i className="bi-plus-circle me-2" />
                    Crear evento
                  </Button>
                )}
              </div>
            </div>

          <section className="AEP-stats-section">
            <div className="AEP-stats-grid">
              <StatsCard
                icon="bi-check-circle-fill"
                label="Eventos activos"
                value={loadingStats ? '--' : stats.activeEvents}
                variant="success"
              />
              <StatsCard
                icon="bi-pause-circle-fill"
                label="Eventos inactivos"
                value={loadingStats ? '--' : stats.inactiveEvents}
                variant="warning"
              />
              <StatsCard
                icon="bi-archive-fill"
                label="Eventos archivados"
                value={loadingStats ? '--' : stats.archivedEvents}
                variant="muted"
              />
            </div>
          </section>

          <section className="AEP-search-section">
            <div className="AEP-search-card">
              <div className="AEP-search-content">
                <Form.Control
                  type="search"
                  placeholder="Buscar por nombre, descripción o ID..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="AEP-search-input"
                />
                <div className="AEP-search-actions">
                  <Button
                    variant="outline-primary"
                    onClick={() => setSearch('')}
                    disabled={!search}
                    size="sm"
                  >
                    Limpiar
                  </Button>
                  <Button variant="primary" onClick={handleRefresh} disabled={isRefreshing} size="sm">
                    {isRefreshing ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Actualizando
                      </>
                    ) : (
                      <>
                        <i className="bi-arrow-repeat me-2" />
                        Actualizar
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <section className="AEP-events-section">
            {loadingEvents ? (
              <div className="AEP-events-grid">
                {Array.from({ length: 6 }).map((_, index) => (
                  <EventSkeleton key={index} />
                ))}
              </div>
            ) : filteredEvents.length === 0 ? (
              <EmptyState onCreate={() => setShowCreateModal(true)} showArchived={showArchived} />
            ) : (
              <div className="AEP-events-grid">
                {filteredEvents.map((event) => {
                  const imageUrl = resolveImageUrl(event.imagenevento);
                  const status = event.activo
                    ? { label: 'Activo', variant: 'success' }
                    : { label: 'Inactivo', variant: 'secondary' };

                  return (
                    <div key={event.id} className="AEP-event-card">
                      {imageUrl ? (
                        <img src={imageUrl} alt={event.tituloevento || event.nombre || 'Evento'} className="AEP-event-image" />
                      ) : (
                        <div className="AEP-event-image-placeholder">
                          <i className="bi-calendar-event" />
                        </div>
                      )}
                      <div className="AEP-event-body">
                        <div className="AEP-event-badges">
                          <Badge bg={status.variant}>{status.label}</Badge>
                          {event.archivo && <Badge bg="dark">Archivado</Badge>}
                        </div>
                        <h3 className="AEP-event-title">{event.tituloevento || event.nombre || 'Evento sin título'}</h3>
                        <p className="AEP-event-description">
                          {event.descripcionbreve || event.descripcionBreve || 'Sin descripción disponible.'}
                        </p>
                        <div className="AEP-event-stats">
                          <span className="AEP-event-stat-item">
                            <i className="bi-calendar3" />
                            {formatEventDate(event.diaseventos)}
                          </span>
                          <span className="AEP-event-stat-item">
                            <i className="bi-people" />
                            {event.classesCount ?? 0} clases
                          </span>
                        </div>
                        <Button
                          variant="primary"
                          className="AEP-event-action"
                          onClick={() => navigate(`/admin/events/${event.id}`)}
                        >
                          <i className="bi-box-arrow-in-right me-2" />
                          Ver detalle
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
          </div>
        </main>
      </div>

      <Modal show={showCreateModal} onHide={() => {
        setShowCreateModal(false);
        setCreateEventTitle('');
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi-plus-circle me-2" />
            Crear Nuevo Evento
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={(e) => {
          e.preventDefault();
          handleCreateEvent();
        }}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Título del Evento *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ingresa el título del evento"
                value={createEventTitle}
                onChange={(event) => setCreateEventTitle(event.target.value)}
                required
                autoFocus
                maxLength={100}
                disabled={creatingEvent}
              />
              <Form.Text className="text-muted">
                El evento se creará en estado inactivo. Podrás configurarlo después.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowCreateModal(false);
              setCreateEventTitle('');
            }} disabled={creatingEvent}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={creatingEvent || !createEventTitle.trim()}>
              {creatingEvent ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  Creando...
                </>
              ) : (
                <>
                  <i className="bi-check-circle me-2" />
                  Crear Evento
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

const AdminEventosPage = () => {
  return (
    <AdminNotificationsProvider>
      <AdminEventosPageInner />
    </AdminNotificationsProvider>
  );
};

export default AdminEventosPage;

